import type * as ecModels from '../../../models'

import type { Config } from './config'
import type * as models from './models'
/**
 * {@link https://ngrx.io/docs State} of this plugin
 */
export interface State<
  TTypeParams extends
    ecModels.TypeParams.Constraint = ecModels.TypeParams.Constraint,
> {
  /**
   * Lookup of {@link models.Group} by their {@link models.Group.id}
   *
   * @see {@link models.Group[]}
   */
  groups: Record<models.Group.Id, models.Group<TTypeParams>>
}
export namespace State {
  /**
   * Produces the initial {@link State} using the given {@link models.Config}
   */
  export function getInitialState<
    TTypeParams extends ecModels.TypeParams.Constraint,
  >(config?: Config<TTypeParams>): State<TTypeParams> {
    return {
      groups: Object.fromEntries(
        config?.initialGroups.map(group => [group.id, group]) ?? [],
      ),
    }
  }

  /**
   * Puts one or more {@link models.Group} into the cache {@link State}
   */
  export function put<TTypeParams extends ecModels.TypeParams.Constraint>(
    state: State<TTypeParams>,
    ...groups: readonly models.Group<TTypeParams>[]
  ) {
    if (!groups.length) {
      // nothing to do
      return state
    }

    state = { ...state }
    state.groups = { ...state.groups }

    for (const group of groups) {
      state.groups[group.id] = group
    }

    return state
  }

  /**
   * Removes one or more {@link models.Group} from the cache {@link State}
   */
  export function _delete(
    state: State<any>,
    ...ids: readonly models.Group.Id[]
  ) {
    if (!ids.length) {
      // nothing to do
      return state
    }

    state = { ...state }
    state.groups = { ...state.groups }

    for (const id of ids) {
      delete state.groups[id]
    }

    return state
  }

  export function associate(
    state: State<any>,
    {
      entityIds,
      groupIds,
    }: {
      readonly groupIds: readonly models.Group.Id[]
      readonly entityIds: readonly ecModels.EntityId[]
    },
  ) {
    state = { ...state }
    state.groups = { ...state.groups }

    for (const groupId of groupIds) {
      const group = state.groups[groupId]
      if (group.entitySelector.ids) {
        state.groups[groupId] = {
          ...group,
          entitySelector: {
            ids: [...group.entitySelector.ids, ...entityIds],
          },
        }
      } else {
        console.warn(
          `Cannot associate entities from group: ${group.name} (${group.id})`,
        )
      }
    }

    return state
  }

  export function unassociate(
    state: State<any>,
    {
      entityIds,
      groupIds,
    }: {
      readonly groupIds: readonly models.Group.Id[]
      readonly entityIds: readonly ecModels.EntityId[]
    },
  ) {
    const entityIdSet = new Set(entityIds)
    state = { ...state }
    state.groups = { ...state.groups }

    for (const groupId of groupIds) {
      const group = state.groups[groupId]
      if (group.entitySelector.ids) {
        state.groups[groupId] = {
          ...group,
          entitySelector: {
            ids: group.entitySelector.ids.filter(id => !entityIdSet.has(id)),
          },
        }
      } else {
        console.warn(
          `Cannot unassociate entities from group: ${group.name} (${group.id})`,
        )
      }
    }

    return state
  }
}
